import { useRef, type RefObject } from 'react';

/**
 * Where focus lands when a dialog or a drawer opens: its first field, so a short
 * form can be typed into at once (docs/11 §7). With no field it leaves Radix to
 * focus the first control it finds.
 */
function focusFirstField(container: HTMLElement | null, event: Event): void {
    const field = container?.querySelector<HTMLElement>(
        'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled])',
    );
    if (!field) return;
    event.preventDefault();
    field.focus();
}

/**
 * A dialog's or a drawer's focus on the way in and on the way out: its first
 * field when it opens, and back on whatever opened it when it closes.
 *
 * Radix hands the focus back only to a `Dialog.Trigger`. Ours open from state —
 * a row's button, a menu item, a confirmation asked for in code — so the focus
 * fell to the page's body and a keyboard user started again from the top.
 * The opener is what held the focus as the dialog mounted; one that has since
 * left the page (the row it sat on was deleted) is let go, and Radix's own
 * fallback stands.
 */
export function useDialogFocus(content: RefObject<HTMLElement | null>) {
    const opener = useRef<HTMLElement | null>(null);
    return {
        onOpenAutoFocus(event: Event) {
            opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
            focusFirstField(content.current, event);
        },
        onCloseAutoFocus(event: Event) {
            const target = opener.current;
            opener.current = null;
            if (!target?.isConnected) return;
            event.preventDefault();
            target.focus();
        },
    };
}
